import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { isChunkLoadError, reloadOnceForChunkError } from "../../utils/lazyWithRetry";

interface QuizErrorBoundaryProps {
  children: ReactNode;
}

interface QuizErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class QuizErrorBoundary extends Component<
  QuizErrorBoundaryProps,
  QuizErrorBoundaryState
> {
  constructor(props: QuizErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): QuizErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // A deploy changed the chunk hashes under this tab. Reload once to pick
    // up the new build instead of showing an error the player can't fix.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;
    console.error("[QuizErrorBoundary] Quiz crashed:", error);
    console.error("[QuizErrorBoundary] Component stack:", errorInfo.componentStack);
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
  }

  handleReset = (): void => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  handleReturnToDashboard = (): void => {
    window.location.href = "/play";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary error-boundary--quiz">
          <div className="error-boundary__card">
            <div className="error-boundary__icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3m.08 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="error-boundary__title">
              The quiz hit a snag!
            </h2>
            <p className="error-boundary__message">
              No worries — your progress has been saved. Let's jump back in!
            </p>
            <div className="error-boundary__actions">
              <button
                className="error-boundary__button"
                onClick={this.handleReset}
              >
                Try Again
              </button>
              <button
                className="error-boundary__button error-boundary__button--secondary"
                onClick={this.handleReturnToDashboard}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default QuizErrorBoundary;
